import {
  animate,
  animateChild,
  group,
  query,
  stagger,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  ContentChild,
  Directive,
  Input,
  TemplateRef,
} from '@angular/core';
import { ExtractExpr, ItemExprUtils } from '../../utils/item-expr.utils';
import { TranslationService } from '../../services/translation.service';

interface ListItemContext<T = any> {
  index: number;
  item: T;
  key: any;
}

interface ListContainerContext<T = any> {
  itemListTemplate: TemplateRef<ListItemContext<T>>;
}

@Directive({
  selector: 'ng-template[listItem]',
})
export class ListItemTemplate<T = any> {
  constructor(public templateRef: TemplateRef<ListItemContext<T>>) {}
}

@Directive({
  selector: 'ng-template[listContainer]',
})
export class ListContainerTemplate<T = any> {
  constructor(public templateRef: TemplateRef<ListContainerContext<T>>) {}
}

@Directive({
  selector: 'ng-template[listEmpty]',
})
export class ListEmptyTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-list',
  templateUrl: 'list.component.html',
  styleUrls: ['list.component.scss'],
  animations: [
    trigger('listAnimation', [
      transition(
        '* => *',
        group([
          // each time the binding value changes
          query(
            ':leave',
            animate('0.1s', style({ opacity: 0, transform: 'scale(0.5)' })),
            { optional: true }
          ),
          query(
            ':enter',
            [
              style({ opacity: 0, transform: 'scale(0.5)' }),
              stagger(50, [
                animate('0.15s', style({ opacity: 1, transform: 'scale(1)' })),
              ]),
            ],
            { optional: true }
          ),
          query('[emptyMessage]', animateChild(), { optional: true }),
        ])
      ),
    ]),
    trigger('emptyMessage', [
      state('*', style({ opacity: 0, transform: 'scale(0.5)' })),
      state('true', style({ opacity: 1, transform: 'scale(1)' })),
      transition(
        '* => true',
        animate('0.15s 0.1s', style({ opacity: 1, transform: 'scale(1)' }))
      ),
    ]),
  ],
})
export class ListComponent<T = any> {
  @ContentChild(ListEmptyTemplate, { static: false })
  emptyListTplRef: ListEmptyTemplate;
  @ContentChild(ListContainerTemplate, { static: false })
  containerTplRef: ListContainerTemplate<T>;
  @ContentChild(ListItemTemplate, { static: false })
  itemTplRef: ListItemTemplate<T>;

  @Input()
  items: T[] = [];
  @Input()
  keyExpr: ExtractExpr<T>;

  @Input()
  animate = true;

  @Input()
  scrollable = true;
  @Input()
  vertical = true;
  @Input()
  horizontal = false;

  constructor(private translationService: TranslationService) {}

  get emptyMessage(): string {
    return this.translationService.getCurrentLanguage()['no-data'];
  }

  isEmptyList() {
    return !this.items?.length;
  }

  trackByInternal = (index: number, item: T) => {
    return ItemExprUtils.extractValue(item, index, this.keyExpr);
  };
}
